import React, { useState, useEffect } from 'react';
import { X, Plus } from 'lucide-react';
import { ProfileRead, ProfileWithHorseCount } from '../types/api';
import { apiService } from '../services/api';

interface ProfileSelectionPopupProps {
  isOpen: boolean;
  horseId: string;
  profiles: ProfileWithHorseCount[];
  onConfirm: (selectedProfileIds: string[]) => void;
  onClose: () => void;
  onProfileCreated: (newProfile: ProfileRead) => void;
}

export const ProfileSelectionPopup: React.FC<ProfileSelectionPopupProps> = ({
  isOpen,
  horseId,
  profiles,
  onConfirm,
  onClose,
  onProfileCreated,
}) => {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showInlineCreate, setShowInlineCreate] = useState(false);
  const [newProfileName, setNewProfileName] = useState('');
  const [newProfileDesc, setNewProfileDesc] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [assigning, setAssigning] = useState(false);

  // Reset state whenever popup opens
  useEffect(() => {
    if (isOpen) {
      setSelectedIds(new Set());
      setShowInlineCreate(false);
      setNewProfileName('');
      setNewProfileDesc('');
      setCreateError(null);
    }
  }, [isOpen]);

  const toggleProfile = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleCreateInline = async () => {
    if (!newProfileName.trim()) return;
    setCreating(true);
    setCreateError(null);
    try {
      const created = await apiService.createProfile({
        name: newProfileName.trim(),
        description: newProfileDesc.trim() || undefined,
      });
      onProfileCreated(created);
      setSelectedIds(prev => new Set(Array.from(prev).concat(created.id)));
      setShowInlineCreate(false);
      setNewProfileName('');
      setNewProfileDesc('');
    } catch (e) {
      setCreateError(e instanceof Error ? e.message : 'Failed to create profile');
    } finally {
      setCreating(false);
    }
  };

  const handleAssign = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) {
      onConfirm([]);
      return;
    }
    setAssigning(true);
    try {
      await apiService.assignProfilesToHorse(horseId, ids);
      onConfirm(ids);
    } catch (e) {
      // Non-fatal: still close the popup
      onConfirm(ids);
    } finally {
      setAssigning(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/40 z-[9998]"
        onClick={onClose}
      />

      {/* Bottom sheet (mobile) / centered modal (desktop) */}
      <div
        className="fixed bottom-0 left-0 right-0
                   lg:bottom-auto lg:left-1/2 lg:-translate-x-1/2 lg:top-1/2 lg:-translate-y-1/2
                   lg:max-w-sm lg:w-full lg:mx-4
                   z-[9999] bg-white rounded-t-3xl lg:rounded-2xl shadow-2xl"
        style={{
          paddingBottom: 'max(env(safe-area-inset-bottom), 16px)',
        }}
      >
        {/* Handle — mobile only */}
        <div className="lg:hidden flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 rounded-full bg-gray-200" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-4 lg:pt-5 pb-3">
          <div>
            <h3 className="text-base font-semibold text-[#132B64]">Add to a Profile</h3>
            <p className="text-xs text-gray-400 mt-0.5">Optional — you can skip this</p>
          </div>
          <button
            onClick={onClose}
            className="h-8 w-8 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-400"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Profile list */}
        <div className="px-5 flex flex-col gap-2 max-h-60 overflow-y-auto">
          {profiles.length === 0 && !showInlineCreate && (
            <p className="text-sm text-gray-400 py-2">
              No profiles yet. Create one below.
            </p>
          )}

          {profiles.map(profile => (
            <label
              key={profile.id}
              className="flex items-center gap-3 py-2.5 cursor-pointer"
            >
              <input
                type="checkbox"
                checked={selectedIds.has(profile.id)}
                onChange={() => toggleProfile(profile.id)}
                className="h-4 w-4 rounded border-gray-300 text-[#132B64] focus:ring-[#132B64] accent-[#132B64]"
              />
              <div className="flex-1 min-w-0">
                <span className="text-sm font-medium text-gray-900">{profile.name}</span>
                {profile.description && (
                  <span className="block text-xs text-gray-400 truncate">{profile.description}</span>
                )}
              </div>
              <span className="text-xs text-gray-400 shrink-0">{profile.horse_count}</span>
            </label>
          ))}

          {/* Inline create form */}
          {showInlineCreate && (
            <div className="border border-gray-200 rounded-xl p-3 flex flex-col gap-2 mt-1">
              <input
                type="text"
                value={newProfileName}
                onChange={e => setNewProfileName(e.target.value)}
                placeholder="Profile name"
                maxLength={100}
                autoFocus
                className="text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#132B64]/30 focus:border-[#132B64]"
              />
              <input
                type="text"
                value={newProfileDesc}
                onChange={e => setNewProfileDesc(e.target.value)}
                placeholder="Description (optional)"
                maxLength={500}
                className="text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#132B64]/30 focus:border-[#132B64]"
              />
              {createError && (
                <p className="text-xs text-red-600">{createError}</p>
              )}
              <div className="flex gap-2">
                <button
                  onClick={() => { setShowInlineCreate(false); setCreateError(null); }}
                  className="flex-1 py-1.5 text-xs font-semibold text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateInline}
                  disabled={creating || !newProfileName.trim()}
                  className="flex-1 py-1.5 text-xs font-semibold text-white bg-[#132B64] rounded-lg disabled:opacity-50 hover:bg-[#0f2350] transition-colors"
                >
                  {creating ? 'Creating…' : 'Create & select'}
                </button>
              </div>
            </div>
          )}

          {/* Create new button */}
          {!showInlineCreate && (
            <button
              onClick={() => setShowInlineCreate(true)}
              className="flex items-center gap-2 py-2.5 text-sm text-[#132B64] font-medium hover:opacity-70 transition-opacity"
            >
              <Plus size={16} />
              Create new profile
            </button>
          )}
        </div>

        {/* Footer buttons */}
        <div className="flex gap-3 px-5 pt-4">
          <button
            onClick={() => onConfirm([])}
            className="flex-1 py-3 text-sm font-semibold text-gray-600 border border-gray-200 rounded-full hover:bg-gray-50 transition-colors"
          >
            Skip
          </button>
          <button
            onClick={handleAssign}
            disabled={assigning || selectedIds.size === 0}
            className="flex-1 py-3 text-sm font-semibold text-white bg-[#132B64] rounded-full disabled:opacity-50 hover:bg-[#0f2350] transition-colors"
          >
            {assigning ? 'Assigning…' : `Assign${selectedIds.size > 0 ? ` (${selectedIds.size})` : ''}`}
          </button>
        </div>
      </div>
    </>
  );
};
